import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { CallMetrics, TransferMetricsByAgent } from '../../../interfaces/calls';
import { downloadFile, metricsToCSV } from '../../../utils/exportUtils';
import { CustomTable } from '../../common/CustomTable';
import { ExpandableSection } from './ExpandableSection';

// Helper to create metric label with tooltip
const MetricLabelWithTooltip = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
    {label}
    <Tooltip title={tooltip} arrow placement='top'>
      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active', cursor: 'help' }} />
    </Tooltip>
  </Box>
);

interface OverviewTabProps {
  metrics: CallMetrics;
  comparison?: CallMetrics;
  renderMetricCard: (
    label: string | ReactNode,
    value: string | number,
    prevValue?: string | number,
    numericValue?: number,
    inverted?: boolean,
    formatter?: (val: number) => string,
  ) => ReactNode;
  formatDuration: (seconds: number) => string;
  formatWithComparison: (
    current: number | string,
    previous: number | string | undefined,
  ) => ReactNode;
  currentStartDate?: string;
  currentEndDate?: string;
  previousStartDate?: string;
  previousEndDate?: string;
}

export const OverviewTab = ({
  metrics,
  comparison,
  renderMetricCard,
  formatDuration,
  formatWithComparison,
  currentStartDate,
  currentEndDate,
  previousStartDate,
  previousEndDate,
}: OverviewTabProps) => {
  const [inboundExpanded, setInboundExpanded] = useState(true);
  const [outboundExpanded, setOutboundExpanded] = useState(true);
  const [qualityExpanded, setQualityExpanded] = useState(true);
  const [transferExpanded, setTransferExpanded] = useState(true);
  const [ivrExpanded, setIvrExpanded] = useState(true);

  const handleExportKeyMetrics = () => {
    if (!currentStartDate || !currentEndDate) return;

    const keyMetrics = [
      { label: 'Total Calls', value: metrics.totalCalls, previousValue: comparison?.totalCalls },
      { label: 'Inbound', value: metrics.inboundCalls, previousValue: comparison?.inboundCalls },
      { label: 'Outbound', value: metrics.outboundCalls, previousValue: comparison?.outboundCalls },
      { label: 'Answered', value: metrics.answeredCalls, previousValue: comparison?.answeredCalls },
      {
        label: 'Abandoned',
        value: metrics.abandonedCalls,
        previousValue: comparison?.abandonedCalls,
      },
      {
        label: 'Average Duration (seconds)',
        value: metrics.averageDuration,
        previousValue: comparison?.averageDuration,
      },
      {
        label: 'Average Wait Time (seconds)',
        value: metrics.averageWaitTime,
        previousValue: comparison?.averageWaitTime,
      },
      {
        label: 'Average Answer Time (seconds)',
        value: metrics.averageAnswerTime,
        previousValue: comparison?.averageAnswerTime,
      },
    ];

    const csv = metricsToCSV(
      keyMetrics,
      currentStartDate,
      currentEndDate,
      previousStartDate,
      previousEndDate,
    );
    downloadFile(csv, 'calls-key-metrics.csv');
  };

  const handleExportInbound = () => {
    if (!currentStartDate || !currentEndDate || !metrics.inboundDetails) return;

    const inboundMetrics = [
      {
        label: 'Answered',
        value: metrics.inboundDetails.answered,
        previousValue: comparison?.inboundDetails?.answered,
      },
      {
        label: 'Missed',
        value: metrics.inboundDetails.missed,
        previousValue: comparison?.inboundDetails?.missed,
      },
      {
        label: 'Abandoned',
        value: metrics.inboundDetails.abandoned,
        previousValue: comparison?.inboundDetails?.abandoned,
      },
      {
        label: 'Answer Rate (%)',
        value: metrics.inboundDetails.answerRate?.toFixed(1) ?? '0.0',
        previousValue: comparison?.inboundDetails?.answerRate?.toFixed(1),
      },
      {
        label: 'Abandon Rate (%)',
        value: metrics.inboundDetails.abandonRate?.toFixed(1) ?? '0.0',
        previousValue: comparison?.inboundDetails?.abandonRate?.toFixed(1),
      },
    ];

    const csv = metricsToCSV(
      inboundMetrics,
      currentStartDate,
      currentEndDate,
      previousStartDate,
      previousEndDate,
    );
    downloadFile(csv, 'calls-inbound-metrics.csv');
  };

  const handleExportOutbound = () => {
    if (!currentStartDate || !currentEndDate || !metrics.outboundDetails) return;

    const outboundMetrics = [
      {
        label: 'Completed',
        value: metrics.outboundDetails.completed,
        previousValue: comparison?.outboundDetails?.completed,
      },
      {
        label: 'No Answer',
        value: metrics.outboundDetails.noAnswer,
        previousValue: comparison?.outboundDetails?.noAnswer,
      },
      {
        label: 'Failed',
        value: metrics.outboundDetails.failed,
        previousValue: comparison?.outboundDetails?.failed,
      },
      {
        label: 'Success Rate (%)',
        value: metrics.outboundDetails.successRate?.toFixed(1) ?? '0.0',
        previousValue: comparison?.outboundDetails?.successRate?.toFixed(1),
      },
    ];

    const csv = metricsToCSV(
      outboundMetrics,
      currentStartDate,
      currentEndDate,
      previousStartDate,
      previousEndDate,
    );
    downloadFile(csv, 'calls-outbound-metrics.csv');
  };

  const handleExportQuality = () => {
    if (!currentStartDate || !currentEndDate || !metrics.ratingMetrics) return;

    const qualityMetrics = [
      {
        label: 'Evaluated Calls',
        value: metrics.ratingMetrics.evaluatedCalls,
        previousValue: comparison?.ratingMetrics?.evaluatedCalls,
      },
      {
        label: 'Unevaluated Calls',
        value: metrics.ratingMetrics.unevaluatedCalls,
        previousValue: comparison?.ratingMetrics?.unevaluatedCalls,
      },
      {
        label: 'Average Rating',
        value: metrics.ratingMetrics.averageRating,
        previousValue: comparison?.ratingMetrics?.averageRating,
      },
      {
        label: 'Positive Rating Rate',
        value: `${metrics.ratingMetrics.positiveRatingRate?.toFixed(1) ?? '0.0'}%`,
        previousValue: comparison?.ratingMetrics?.positiveRatingRate
          ? `${comparison.ratingMetrics.positiveRatingRate.toFixed(1)}%`
          : undefined,
      },
    ];

    const csv = metricsToCSV(
      qualityMetrics,
      currentStartDate,
      currentEndDate,
      previousStartDate,
      previousEndDate,
    );
    downloadFile(csv, 'calls-quality-metrics.csv');
  };

  const handleExportTransfer = () => {
    if (!currentStartDate || !currentEndDate || !metrics.transferMetrics) {
      return;
    }

    const tm = metrics.transferMetrics;
    const ctm = comparison?.transferMetrics;

    const transferMetricsData = [
      { label: 'Total Transfers', value: tm.totalTransfers, previousValue: ctm?.totalTransfers },
      { label: 'Blind Transfers', value: tm.blindTransfers, previousValue: ctm?.blindTransfers },
      { label: 'Warm Transfers', value: tm.warmTransfers, previousValue: ctm?.warmTransfers },
      {
        label: 'Completed Transfers',
        value: tm.completedTransfers,
        previousValue: ctm?.completedTransfers,
      },
      { label: 'Failed Transfers', value: tm.failedTransfers, previousValue: ctm?.failedTransfers },
    ];

    const csv = metricsToCSV(
      transferMetricsData,
      currentStartDate,
      currentEndDate,
      previousStartDate,
      previousEndDate,
    );
    downloadFile(csv, 'calls-transfer-metrics.csv');
  };

  const handleExportIvr = () => {
    if (!currentStartDate || !currentEndDate) {
      return;
    }

    const ivrMetrics = [
      {
        label: 'Direct to Agent',
        value: metrics.directToAgentCalls ?? 0,
        previousValue: comparison?.directToAgentCalls,
      },
      {
        label: 'IVR Routed',
        value: metrics.ivrRoutedCalls ?? 0,
        previousValue: comparison?.ivrRoutedCalls,
      },
      {
        label: 'After Hours Calls',
        value: metrics.afterHoursCalls ?? 0,
        previousValue: comparison?.afterHoursCalls,
      },
    ];

    const csv = metricsToCSV(
      ivrMetrics,
      currentStartDate,
      currentEndDate,
      previousStartDate,
      previousEndDate,
    );
    downloadFile(csv, 'calls-ivr-routing-metrics.csv');
  };

  return (
    <Box>
      {/* Key Metrics - Always Visible */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
          mb: 2,
        }}>
        <Typography variant='h6'>🎯 Key Metrics</Typography>
        <Tooltip title='Export key metrics'>
          <IconButton size='small' onClick={handleExportKeyMetrics} sx={{ color: '#1a667a' }}>
            <DownloadIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard('Total Calls', metrics.totalCalls, comparison?.totalCalls)}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard('Inbound', metrics.inboundCalls, comparison?.inboundCalls)}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard('Outbound', metrics.outboundCalls, comparison?.outboundCalls)}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard('Answered', metrics.answeredCalls, comparison?.answeredCalls)}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Avg Duration',
            formatDuration(metrics.averageDuration),
            comparison?.averageDuration,
            metrics.averageDuration,
            true,
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Avg Wait Time',
            formatDuration(metrics.averageWaitTime),
            comparison?.averageWaitTime,
            metrics.averageWaitTime,
            true,
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Avg Answer Time',
            formatDuration(metrics.averageAnswerTime),
            comparison?.averageAnswerTime,
            metrics.averageAnswerTime,
            true,
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'With Recordings',
            metrics.callsWithRecordings,
            comparison?.callsWithRecordings,
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard('Transferred', metrics.transferredCalls, comparison?.transferredCalls)}
        </Grid>
      </Grid>

      {/* Inbound Breakdown */}
      {metrics.inboundDetails && (
        <ExpandableSection
          title='📞 Inbound Breakdown'
          expanded={inboundExpanded}
          onToggle={() => setInboundExpanded(!inboundExpanded)}
          onExport={handleExportInbound}>
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                <MetricLabelWithTooltip
                  label='Inbound Answered'
                  tooltip='Calls where an agent picked up'
                />,
                metrics.inboundDetails.answered,
                comparison?.inboundDetails?.answered,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                <MetricLabelWithTooltip
                  label='Inbound Abandoned'
                  tooltip='Calls where the client hung up before 30 seconds of wait time'
                />,
                metrics.inboundDetails.abandoned,
                comparison?.inboundDetails?.abandoned,
                undefined,
                true,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                <MetricLabelWithTooltip
                  label='Inbound Missed'
                  tooltip='Calls not answered because no agent was available'
                />,
                metrics.inboundDetails.missed,
                comparison?.inboundDetails?.missed,
                undefined,
                true,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                <MetricLabelWithTooltip
                  label='Inbound Rejected'
                  tooltip='Calls that an agent actively declined to answer'
                />,
                metrics.inboundDetails.rejected,
                comparison?.inboundDetails?.rejected,
                undefined,
                true,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Answer Rate',
                `${metrics.inboundDetails.answerRate.toFixed(1)}%`,
                comparison?.inboundDetails?.answerRate,
                metrics.inboundDetails.answerRate,
                false,
                (val: number) => `${val.toFixed(1)}%`,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Abandon Rate',
                `${metrics.inboundDetails.abandonRate.toFixed(1)}%`,
                comparison?.inboundDetails?.abandonRate,
                metrics.inboundDetails.abandonRate,
                true,
                (val: number) => `${val.toFixed(1)}%`,
              )}
            </Grid>
          </Grid>
        </ExpandableSection>
      )}

      {/* Outbound Breakdown */}
      {metrics.outboundDetails && (
        <ExpandableSection
          title='📤 Outbound Breakdown'
          expanded={outboundExpanded}
          onToggle={() => setOutboundExpanded(!outboundExpanded)}
          onExport={handleExportOutbound}>
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                <MetricLabelWithTooltip
                  label='Outbound Completed'
                  tooltip='Outbound calls that were successfully connected and completed'
                />,
                metrics.outboundDetails.completed,
                comparison?.outboundDetails?.completed,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                <MetricLabelWithTooltip
                  label='Outbound Failed'
                  tooltip='Outbound calls that failed to connect due to system or network issues'
                />,
                metrics.outboundDetails.failed,
                comparison?.outboundDetails?.failed,
                undefined,
                true,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                <MetricLabelWithTooltip
                  label='No Answer'
                  tooltip='Outbound calls where the recipient did not pick up'
                />,
                metrics.outboundDetails.noAnswer,
                comparison?.outboundDetails?.noAnswer,
                undefined,
                true,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Success Rate',
                `${metrics.outboundDetails.successRate.toFixed(1)}%`,
                comparison?.outboundDetails?.successRate,
                metrics.outboundDetails.successRate,
                false,
                (val: number) => `${val.toFixed(1)}%`,
              )}
            </Grid>
          </Grid>
        </ExpandableSection>
      )}

      {/* Quality Metrics */}
      {metrics.ratingMetrics && (
        <ExpandableSection
          title='⭐ Quality Metrics'
          expanded={qualityExpanded}
          onToggle={() => setQualityExpanded(!qualityExpanded)}
          onExport={handleExportQuality}>
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Evaluated Calls',
                metrics.ratingMetrics?.evaluatedCalls,
                comparison?.ratingMetrics?.evaluatedCalls,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Unevaluated',
                metrics.ratingMetrics?.unevaluatedCalls,
                comparison?.ratingMetrics?.unevaluatedCalls,
                undefined,
                true,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Avg Rating',
                metrics.ratingMetrics?.averageRating?.toFixed(2),
                comparison?.ratingMetrics?.averageRating,
                metrics.ratingMetrics?.averageRating,
                false,
                (val: number) => val.toFixed(2),
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Positive Rate',
                `${metrics.ratingMetrics?.positiveRatingRate?.toFixed(1) ?? '0.0'}%`,
                comparison?.ratingMetrics?.positiveRatingRate,
                metrics.ratingMetrics?.positiveRatingRate,
                false,
                (val: number) => `${val.toFixed(1)}%`,
              )}
            </Grid>
          </Grid>
        </ExpandableSection>
      )}

      {/* Transfer Breakdown */}
      {metrics.transferMetrics && metrics.transferMetrics.totalTransfers > 0 && (
        <ExpandableSection
          title='🔄 Transfer Breakdown'
          expanded={transferExpanded}
          onToggle={() => setTransferExpanded(!transferExpanded)}
          onExport={handleExportTransfer}>
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Total Transfers',
                metrics.transferMetrics.totalTransfers,
                comparison?.transferMetrics?.totalTransfers,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Blind Transfers',
                metrics.transferMetrics.blindTransfers,
                comparison?.transferMetrics?.blindTransfers,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Warm Transfers',
                metrics.transferMetrics.warmTransfers,
                comparison?.transferMetrics?.warmTransfers,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Completed',
                metrics.transferMetrics.completedTransfers,
                comparison?.transferMetrics?.completedTransfers,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Failed',
                metrics.transferMetrics.failedTransfers,
                comparison?.transferMetrics?.failedTransfers,
                undefined,
                true,
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Success Rate',
                `${
                  metrics.transferMetrics.totalTransfers > 0
                    ? (
                        (metrics.transferMetrics.completedTransfers /
                          metrics.transferMetrics.totalTransfers) *
                        100
                      ).toFixed(1)
                    : '0.0'
                }%`,
                comparison?.transferMetrics?.totalTransfers &&
                  comparison.transferMetrics.totalTransfers > 0
                  ? (comparison.transferMetrics.completedTransfers /
                      comparison.transferMetrics.totalTransfers) *
                      100
                  : undefined,
                metrics.transferMetrics.totalTransfers > 0
                  ? (metrics.transferMetrics.completedTransfers /
                      metrics.transferMetrics.totalTransfers) *
                      100
                  : 0,
                false,
                (val: number) => `${val.toFixed(1)}%`,
              )}
            </Grid>
          </Grid>

          {/* Transfers by Agent Table */}
          {metrics.transferMetrics.transfersByAgent &&
            metrics.transferMetrics.transfersByAgent.length > 0 && (
              <Grid container spacing={3} mb={2}>
                <Grid item xs={12}>
                  <Typography
                    variant='subtitle2'
                    gutterBottom
                    sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                    📊 Transfers by Agent
                  </Typography>
                  <CustomTable
                    columns={[
                      { key: 'agentName', label: 'Agent' },
                      { key: 'transfersInitiated', label: 'Initiated' },
                      { key: 'blindInitiated', label: 'Blind Initiated' },
                      { key: 'warmInitiated', label: 'Warm Initiated' },
                      { key: 'transfersReceived', label: 'Received' },
                      { key: 'blindReceived', label: 'Blind Received' },
                      { key: 'warmReceived', label: 'Warm Received' },
                    ]}
                    rows={metrics.transferMetrics.transfersByAgent.map(
                      (agent: TransferMetricsByAgent) => {
                        const prevAgent = comparison?.transferMetrics?.transfersByAgent?.find(
                          (a: TransferMetricsByAgent) => a.agentId === agent.agentId,
                        );
                        return {
                          agentName: agent.agentName,
                          transfersInitiated: formatWithComparison(
                            agent.transfersInitiated,
                            prevAgent?.transfersInitiated,
                          ),
                          blindInitiated: formatWithComparison(
                            agent.blindTransfersInitiated,
                            prevAgent?.blindTransfersInitiated,
                          ),
                          warmInitiated: formatWithComparison(
                            agent.warmTransfersInitiated,
                            prevAgent?.warmTransfersInitiated,
                          ),
                          transfersReceived: formatWithComparison(
                            agent.transfersReceived,
                            prevAgent?.transfersReceived,
                          ),
                          blindReceived: formatWithComparison(
                            agent.blindTransfersReceived,
                            prevAgent?.blindTransfersReceived,
                          ),
                          warmReceived: formatWithComparison(
                            agent.warmTransfersReceived,
                            prevAgent?.warmTransfersReceived,
                          ),
                        };
                      },
                    )}
                    defaultText='No transfer data by agent available'
                    loading={false}
                    exportable={true}
                    exportFilename='calls-transfers-by-agent.csv'
                  />
                </Grid>
              </Grid>
            )}
        </ExpandableSection>
      )}

      {/* Additional Tables */}
      <Grid container spacing={3} sx={{ mt: 4 }}>
        {/* By Status */}
        {metrics.callsByStatus && metrics.callsByStatus.length > 0 && (
          <Grid item xs={12} md={6}>
            <CustomTable
              title='By Status'
              columns={[
                { key: 'status', label: 'Status' },
                { key: 'count', label: 'Count' },
              ]}
              rows={metrics.callsByStatus.map((s: any) => {
                const prevData = comparison?.callsByStatus?.find(
                  (prev: any) => prev.status === s.status,
                );
                return {
                  status: s.status,
                  count: formatWithComparison(s.count, prevData?.count),
                };
              })}
              defaultText='No data available'
              loading={false}
              exportable={true}
              exportFilename='calls-by-status.csv'
            />
          </Grid>
        )}

        {/* Peak Hours */}
        {metrics.peakHours && metrics.peakHours.length > 0 && (
          <Grid item xs={12} md={6}>
            <CustomTable
              title='Peak Hours (PT)'
              columns={[
                { key: 'hour', label: 'Hour' },
                { key: 'count', label: 'Calls' },
              ]}
              rows={metrics.peakHours.map((h: any) => {
                const prevHour = comparison?.peakHours?.find((ph: any) => ph.hour === h.hour);
                return {
                  hour: `${h.hour}:00`,
                  count: formatWithComparison(h.count, prevHour?.count),
                };
              })}
              defaultText='No data available'
              loading={false}
              exportable={true}
              exportFilename='calls-peak-hours.csv'
            />
          </Grid>
        )}

        {/* Rating Distribution */}
        {metrics.ratingMetrics && metrics.ratingMetrics.ratingDistribution && (
          <Grid item xs={12} md={6}>
            <CustomTable
              title='Rating Distribution'
              columns={[
                { key: 'rating', label: 'Rating' },
                { key: 'count', label: 'Count' },
                { key: 'percentage', label: 'Percentage' },
              ]}
              rows={Object.entries(metrics.ratingMetrics.ratingDistribution)
                .sort((a, b) => Number(b[0]) - Number(a[0]))
                .map(([rating, count]) => {
                  const countNum = count as number;
                  const prevData = comparison?.ratingMetrics?.ratingDistribution?.[
                    rating as '1' | '2' | '3' | '4' | '5'
                  ] as number | undefined;
                  const percentage =
                    metrics.ratingMetrics?.evaluatedCalls &&
                    metrics.ratingMetrics.evaluatedCalls > 0
                      ? ((countNum / metrics.ratingMetrics.evaluatedCalls) * 100).toFixed(1)
                      : '0.0';
                  const prevPercentage =
                    comparison?.ratingMetrics?.evaluatedCalls && prevData
                      ? ((prevData / comparison.ratingMetrics.evaluatedCalls) * 100).toFixed(1)
                      : undefined;

                  return {
                    rating: `${'⭐'.repeat(Number(rating))} (${rating})`,
                    count: formatWithComparison(countNum, prevData),
                    percentage: formatWithComparison(
                      `${percentage}%`,
                      prevPercentage ? `${prevPercentage}%` : undefined,
                    ),
                  };
                })}
              defaultText='No rating data available'
              loading={false}
              exportable={true}
              exportFilename='calls-rating-distribution.csv'
            />
          </Grid>
        )}
      </Grid>

      {/* IVR/Routing Metrics */}
      {(metrics.directToAgentCalls !== undefined ||
        metrics.ivrRoutedCalls !== undefined ||
        metrics.callsByIvrRoute) && (
        <ExpandableSection
          title='📞 IVR/Routing Analytics'
          expanded={ivrExpanded}
          onToggle={() => setIvrExpanded(!ivrExpanded)}
          onExport={handleExportIvr}>
          <Grid container spacing={3} mb={4}>
            {metrics.directToAgentCalls !== undefined && (
              <Grid item xs={12} sm={6} md={3}>
                {renderMetricCard(
                  'Direct to Agent',
                  metrics.directToAgentCalls,
                  comparison?.directToAgentCalls,
                )}
              </Grid>
            )}
            {metrics.ivrRoutedCalls !== undefined && (
              <Grid item xs={12} sm={6} md={3}>
                {renderMetricCard('IVR Routed', metrics.ivrRoutedCalls, comparison?.ivrRoutedCalls)}
              </Grid>
            )}
            {metrics.ivrRoutedCalls !== undefined && metrics.directToAgentCalls !== undefined && (
              <Grid item xs={12} sm={6} md={3}>
                {renderMetricCard(
                  'IVR Usage Rate',
                  `${(
                    (metrics.ivrRoutedCalls /
                      (metrics.ivrRoutedCalls + metrics.directToAgentCalls)) *
                    100
                  ).toFixed(1)}%`,
                  comparison?.ivrRoutedCalls && comparison?.directToAgentCalls
                    ? (comparison.ivrRoutedCalls /
                        (comparison.ivrRoutedCalls + comparison.directToAgentCalls)) *
                        100
                    : undefined,
                  (metrics.ivrRoutedCalls / (metrics.ivrRoutedCalls + metrics.directToAgentCalls)) *
                    100,
                  false,
                  (val: number) => `${val.toFixed(1)}%`,
                )}
              </Grid>
            )}
            {metrics.afterHoursCalls !== undefined && (
              <Grid item xs={12} sm={6} md={3}>
                {renderMetricCard(
                  'After Hours Calls',
                  metrics.ivrRoutedCalls !== undefined
                    ? `${metrics.afterHoursCalls} (${(
                        (metrics.afterHoursCalls / metrics.ivrRoutedCalls) *
                        100
                      ).toFixed(1)}%)`
                    : metrics.afterHoursCalls,
                  comparison?.afterHoursCalls,
                  metrics.afterHoursCalls,
                )}
              </Grid>
            )}
          </Grid>

          {/* IVR Routes Table */}
          {metrics.callsByIvrRoute && metrics.callsByIvrRoute.length > 0 && (
            <Grid container spacing={3} mb={2}>
              <Grid item xs={12}>
                <Typography variant='subtitle2' gutterBottom sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                  📊 Breakdown by Route
                </Typography>
                <CustomTable
                  columns={[
                    { key: 'routeName', label: 'Route Name' },
                    { key: 'totalCalls', label: 'Total Calls' },
                    { key: 'completedCalls', label: 'Completed' },
                    { key: 'completionRate', label: 'Completion Rate' },
                    { key: 'avgDuration', label: 'Avg Duration' },
                  ]}
                  rows={metrics.callsByIvrRoute.map((route) => {
                    const prevRoute = comparison?.callsByIvrRoute?.find(
                      (r) => r.routeId === route.routeId,
                    );
                    return {
                      routeName: route.routeName,
                      totalCalls: formatWithComparison(route.totalCalls, prevRoute?.totalCalls),
                      completedCalls: formatWithComparison(
                        route.completedCalls,
                        prevRoute?.completedCalls,
                      ),
                      completionRate: formatWithComparison(
                        `${route.completionRate.toFixed(1)}%`,
                        prevRoute ? `${prevRoute.completionRate.toFixed(1)}%` : undefined,
                      ),
                      avgDuration: formatWithComparison(
                        formatDuration(route.averageDuration),
                        prevRoute?.averageDuration,
                      ),
                    };
                  })}
                  defaultText='No IVR route data available'
                  loading={false}
                  exportable={true}
                  exportFilename='calls-ivr-routes.csv'
                />
              </Grid>

              {/* IVR Options per Route */}
              {metrics.callsByIvrRoute.map((route) => (
                <Grid item xs={12} key={route.routeId}>
                  <Typography
                    variant='subtitle2'
                    gutterBottom
                    sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                    {route.routeName} - Options Breakdown
                  </Typography>
                  <CustomTable
                    columns={[
                      { key: 'option', label: 'Option' },
                      { key: 'count', label: 'Count' },
                      { key: 'percentage', label: '% of Route' },
                      { key: 'completionRate', label: 'Completion Rate' },
                      { key: 'avgDuration', label: 'Avg Duration' },
                    ]}
                    rows={route.callsByOption.map((opt) => {
                      const prevRoute = comparison?.callsByIvrRoute?.find(
                        (r) => r.routeId === route.routeId,
                      );
                      const prevOpt = prevRoute?.callsByOption.find((o) => o.option === opt.option);
                      const percentage =
                        route.totalCalls > 0
                          ? ((opt.count / route.totalCalls) * 100).toFixed(1)
                          : '0.0';
                      const prevPercentage =
                        prevRoute && prevRoute.totalCalls > 0 && prevOpt
                          ? ((prevOpt.count / prevRoute.totalCalls) * 100).toFixed(1)
                          : undefined;

                      // Display logic for different option types
                      let optionDisplay: string;
                      if (opt.option === 'after_hours') {
                        optionDisplay = '🌙 After Hours Voicemail';
                      } else if (opt.option === 'direct') {
                        optionDisplay = '❌ None (Voicemail/Timeout)';
                      } else {
                        optionDisplay = `#️⃣ Option ${opt.option}`;
                      }

                      return {
                        option: optionDisplay,
                        count: formatWithComparison(opt.count, prevOpt?.count),
                        percentage: formatWithComparison(
                          `${percentage}%`,
                          prevPercentage ? `${prevPercentage}%` : undefined,
                        ),
                        completionRate: formatWithComparison(
                          `${opt.completionRate.toFixed(1)}%`,
                          prevOpt ? `${prevOpt.completionRate.toFixed(1)}%` : undefined,
                        ),
                        avgDuration: formatWithComparison(
                          formatDuration(opt.averageDuration),
                          prevOpt?.averageDuration,
                        ),
                      };
                    })}
                    defaultText='No option data available'
                    loading={false}
                    exportable={true}
                    exportFilename={`calls-ivr-route-${route.routeId}-options.csv`}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </ExpandableSection>
      )}
    </Box>
  );
};
